/**
 * System Status panel (storyboard Flow L; ADR-035 / S6) — supersedes System
 * Health.
 *
 * The dockable surface for every operator on the system: a single roll-up view
 * of every top-level component arranged along the flow of data (Frame 1), with
 * near-real-time stoplight updates (snapshot then SSE — storyboard §6). The panel
 * is now **topology-only, full width** — the click-through component/connection
 * detail relocated into the shared **Entity Details ("Selection Details") panel**
 * (ADR-024 §4/§5 amended 2026-07-24; Flow L OQ-1 resolution). Network-parametric
 * composition (the node set derives from the snapshot's reported kinds, not a
 * ``network`` branch — retires GAP-45(i)).
 *
 * **On the selection bus (REQ-UI-051, amended):** selecting a node/connection
 * originates a ``status-component`` / ``status-connection`` selection
 * (:func:`inspectStatus` — the single focus, so it clears a prior map/entity
 * selection and reveals Entity Details); the diagram highlight reads the bus, not
 * local state. The topology stays here; the single-writer live frame that
 * Entity Details resolves the detail against is published by the app-scope
 * :func:`SystemStatusStreamProvider`.
 *
 * **The SSE feed is no longer owned here (REQ-STATUS-048).** It was hoisted to
 * application scope so the top bar's system rollup badge keeps updating while
 * this panel is closed; a badge on a panel-owned stream would freeze at its
 * first value and sit green over a dead feed (REQ-STATUS-016). This component
 * reads it via :func:`useSystemStatusStreamConnection` — one EventSource per
 * tab (REQ-UI-035, REQ-STATUS-062).
 *
 * **Read-visible to every authenticated role** (REQ-STATUS-029 as widened by
 * ADR-036 §D-7 / REQ-PEER-042; Flow L §1 amended 2026-07-24): the BFF gates
 * the endpoints; this component renders a permission-denied state for a session
 * with no known role as defence-in-depth (Flow O semantics). Mutation controls
 * keep their own narrower gates. The header Peer Manager affordance was REMOVED
 * (bootstrap item 7): it existed because a zero-interface deployment composed no
 * peer-manager node, and the Peer Manager node is now structural (REQ-STATUS-041), so
 * selecting it always reaches the "Peers…" affordance on its Selection Detail.
 *
 * Cross-references:
 *   - Flow L (docs/engineering/detail/storyboards/flows/L-system-status).
 *   - ADR-035 (System Status Telemetry); REQ-STATUS-020/021/022/027/029.
 *   - REQ-UI-046 (status palette); REQ-UI-047 (role elision); REQ-UI-051
 *     (selection bus — amended source edge); REQ-UI-059 (landmarks + keyboard);
 *     ADR-024 §2/§4/§5 (amended 2026-07-24).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  groupByKind,
  mergeStreamFrame,
  parseRollupState,
  rollupStateMeta,
  type ResolvedComponent,
  type RollupState,
} from "@/system-status/status-model";
import {
  composeEdges,
  composeInterfaceStack,
  composeNodes,
  composePeerStack,
  CONNECTOR_KINDS,
  isPeerNode,
  type ConfiguredPeer,
  type InterfaceStack,
  type TopologyNode,
} from "@/system-status/status-topology";
import { resolveNodeRender } from "@/system-status/status-node-render";
import { StatusDiagram, type NodeRender } from "@/system-status/status-diagram";
import { StatusPill } from "@/system-status/status-detail-primitives";
import { usePrefersReducedMotion } from "@/system-status/use-reduced-motion";
import { useStatusMotion } from "@/system-status/use-status-motion";
import {
  SYSTEM_STATUS_READER_ROLES,
  useSystemStatusSnapshot,
} from "@/system-status/use-system-status-snapshot";
import {
  useSystemStatusStreamConnection,
  type StatusStreamState,
} from "@/system-status/use-system-status-stream";
import { usePeers } from "@/peers/use-peers";
import { useSelectionActions, useSelectionStore } from "@/selection/selection-store";
import { useMultiPaneApi } from "@/multi-pane/multi-pane-context";
import { useSession } from "@/auth/session-context";
import { cn } from "@/lib/utils";

/** Render the System Status panel (mounted against the ``system-status`` PanelId). */
export function SystemStatusPanel(): React.ReactElement {
  const session = useSession();
  const authorized = session.hasAnyRole(SYSTEM_STATUS_READER_ROLES);

  // Defence-in-depth. Every authenticated role reads this surface
  // (REQ-PEER-042), so in practice this fires only for a session carrying no
  // known Meridian role — all-roles-read is not no-auth-read. The BFF returns
  // 403 for the same session.
  if (!authorized) {
    return <ForbiddenState />;
  }
  return <StatusView />;
}

/** Format an ISO timestamp / epoch-ms as ``HH:MM:SSZ`` (UTC "as of" marker). */
function formatAsOf(value: string | number): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return `${date.toISOString().slice(11, 19)}Z`;
}

/**
 * The authorized panel body: snapshot + SSE wiring + the topology diagram.
 *
 * The snapshot's freshness refresh is gated on this panel's dockview
 * VISIBILITY, not its mountedness — this panel sits behind the map in the
 * default layout's tab group and stays mounted, so the former would poll in
 * every authenticated tab (REQ-STATUS-099; measured live before the gate
 * existed). The gate lives in :func:`useLiveFrameRefresh` via
 * :func:`useOwningPanelVisible`; the dockview wrapper supplies it.
 */
function StatusView(): React.ReactElement {
  const query = useSystemStatusSnapshot();
  // Reader, not owner (REQ-STATUS-048). The EventSource is held by
  // :func:`SystemStatusStreamProvider` at application scope so the top bar's
  // rollup badge keeps updating while this panel is closed; one stream per tab
  // (REQ-UI-035, REQ-STATUS-062).
  const stream = useSystemStatusStreamConnection();
  const { inspectStatus, clear } = useSelectionActions();
  const multiPaneApi = useMultiPaneApi();
  // The node currently inspected as a ``status-component`` (or the owning node of
  // an inspected ``status-connection``, so the topology stays highlighted while
  // the operator drills into a connection). Narrow selectors so the panel
  // re-renders only when the *status* focus moves, not on every bus write.
  const highlightedNodeId = useSelectionStore((state) => {
    const inspected = state.inspected;
    if (inspected?.kind === "status-component") return inspected.id;
    if (inspected?.kind === "status-connection") return inspected.nodeId;
    return null;
  });
  const inspectedComponentId = useSelectionStore((state) =>
    state.inspected?.kind === "status-component" ? state.inspected.id : null,
  );
  // The inspected INTERFACE, in the shape the diagram draws selection from.
  //
  // ⚠ This selector is the fix for a seam defect, not a refactor: `StatusDiagram`
  // has always accepted `selectedInterface` and the panel never passed it, so it
  // defaulted to `null` — an interface icon could be selected on the bus and open
  // its Selection Detail while permanently rendering unselected, and the owning
  // node's containment ring never appeared either. Both components were unit
  // tested with the prop supplied; nothing asserted that the panel supplies it
  // (HANDOFF §5.2 — unit coverage on both sides of an integration point proves
  // nothing about the seam). Identity is composite — a name is only unique
  // within its parent node — matching ADR-024 §4's `(parent_node_id,
  // interface_name)`.
  // Selected as two PRIMITIVES and recombined below, deliberately. A selector
  // returning `{parentId, name}` builds a fresh object on every store read, and
  // the store compares with `Object.is` — so it would never compare equal and the
  // panel would re-render on every unrelated bus write (a map hover, say) while
  // the status focus sat still. Every other selector in this panel is a primitive
  // for the same reason.
  //
  // Both icon levels feed this one pair. A peer icon and an interface icon are
  // the same affordance at the same coordinates, and each node hosts exactly one
  // stack — so `(parentId, name)` addresses whichever the node draws, and a
  // second pair of selectors would only create a way to update one and forget
  // the other.
  const selectedInterfaceParentId = useSelectionStore((state) =>
    state.inspected?.kind === "status-interface" || state.inspected?.kind === "status-peer"
      ? state.inspected.parentNodeId
      : null,
  );
  const selectedInterfaceName = useSelectionStore((state) =>
    state.inspected?.kind === "status-interface"
      ? state.inspected.interfaceName
      : state.inspected?.kind === "status-peer"
        ? state.inspected.peerName
        : null,
  );
  const selectedInterface = useMemo(
    () =>
      selectedInterfaceParentId === null || selectedInterfaceName === null
        ? null
        : { parentId: selectedInterfaceParentId, name: selectedInterfaceName },
    [selectedInterfaceParentId, selectedInterfaceName],
  );

  const snapshot = query.data;

  const merged = useMemo<ResolvedComponent[]>(
    () => (snapshot ? mergeStreamFrame(snapshot, stream.lastFrame) : []),
    [snapshot, stream.lastFrame],
  );
  // Composition takes no reported-kind filter in v2: every node composes on every network,
  // so the display structurally cannot branch on deployment designation (REQ-XSEC-007 /
  // GAP-45(i)) rather than relying on a filter to not branch. WHICH entities back a node
  // still varies by network (an A deployment reports no `cds` provider; a B/C one does) —
  // resolved from the reported data downstream, not by composing a different diagram.
  const nodes = useMemo(() => composeNodes(), []);
  const edges = useMemo(() => composeEdges(), []);
  const byKind = useMemo(() => groupByKind(merged), [merged]);

  // ---- Icon stacks (REQ-STATUS-035..-040, -081) ----------------------------
  // The two halves of an icon come from different places and are joined here:
  // EXISTENCE from the configured roster (so an entity the operator just created
  // appears at once) and HEALTH from the status feed, left-joined by id.
  //
  // ⚠ The Peer Manager's stack enumerates PEERS, not interfaces (REQ-STATUS-081).
  // The peer roster is fetched here, unconditionally, and that is a **deliberate
  // narrowing of REQ-UI-147** rather than the hoist-for-convenience regression
  // that requirement exists to catch. There is no other source for it: a peer
  // with no interfaces never reports, so a feed-only stack would leave it
  // permanently invisible; and interface→peer parentage is privileged as a field
  // (feature-spec §2.10), so a non-privileged reader cannot group the flat
  // interface list under its peers either. The roster is ≤20 rows (REQ-PEER-071),
  // shares its cache key with the management dialog, and -147 now scopes its
  // don't-fetch-until-open rule to the dialog's own per-peer reads.
  //
  // The flat interface list is deliberately NOT fetched here any more. It fed the
  // old interface stack; the peer stack replaces it, and the Selection Detail's
  // interface roster issues it for itself only when a node with an interface layer
  // is actually inspected. One request per surface that needs it, not one on mount
  // for a surface that no longer does (REQ-UI-035).
  const peers = usePeers();
  const rollupsById = useMemo<Map<string, RollupState>>(() => {
    const map = new Map<string, RollupState>();
    for (const component of merged) {
      map.set(component.component_id, parseRollupState(component.rollup_state));
    }
    return map;
  }, [merged]);
  const stacks = useMemo<InterfaceStack[]>(() => {
    const peerRoster: ConfiguredPeer[] = (peers.data ?? []).map((peer) => ({
      name: peer.name,
      // `undefined` (a read that did not count) is normalized to `null` here so the
      // composer's "known zero means unhealthy" rule cannot fire on a missing count.
      interfaceCount: peer.interface_count ?? null,
    }));
    const out: InterfaceStack[] = [];
    for (const node of nodes) {
      if (node.interfaceEdge === undefined) continue;
      // Ingress interfaces are REQ-STATUS-043 [DEFERRED] — no control plane exists for
      // them yet — so the Ingress node composes an empty stack rather than a fabricated
      // one. It stays an *interface* stack: the level is a property of the node, and
      // Ingress does not gain peers by the Peer Manager having them.
      const stack = isPeerNode(node)
        ? composePeerStack(node, peerRoster, rollupsById)
        : composeInterfaceStack(node, [], rollupsById);
      if (stack !== null) out.push(stack);
    }
    return out;
  }, [nodes, peers.data, rollupsById]);

  // The per-node stoplight resolution shared with Entity Details
  // (:func:`resolveNodeRender`) so a selected node's detail pill matches the
  // topology exactly (never a false green — REQ-STATUS-016).
  const renders = useMemo<Map<string, NodeRender>>(() => {
    const map = new Map<string, NodeRender>();
    for (const node of nodes) {
      if (node.planned) continue;
      map.set(node.id, resolveNodeRender(node, byKind));
    }
    return map;
  }, [nodes, byKind]);

  // Select a node onto the bus (ADR-024 §5 amended) — clicking the already-
  // inspected component toggles it off (clear); otherwise it becomes the single
  // focus and Entity Details is revealed if present behind a tab (reveal-only,
  // never force-opened — consistent with the map→list reveal).
  const handleSelect = (id: string): void => {
    if (inspectedComponentId === id) {
      clear();
      return;
    }
    inspectStatus({ kind: "status-component", id });
    multiPaneApi?.activatePanel("entity-details");
  };

  // Per connections-node total, for the live connection-churn burst detector (§11).
  const connectionTotals = useMemo<Map<string, number | null>>(() => {
    const map = new Map<string, number | null>();
    for (const node of nodes) {
      // The Connections satellites are gone (REQ-STATUS-026 superseded) and their roster is
      // re-homed onto the connector node itself (REQ-STATUS-044), so the churn detector keys
      // on that node. Sum across the node's backing kinds: one Peer Manager node now covers several.
      const connectorKinds = node.kinds.filter((kind) => CONNECTOR_KINDS.has(kind));
      if (connectorKinds.length === 0) continue;
      let total: number | null = null;
      for (const kind of connectorKinds) {
        const reported = byKind.get(kind)?.[0]?.connections_total;
        if (reported !== undefined && reported !== null) total = (total ?? 0) + reported;
      }
      map.set(node.id, total);
    }
    return map;
  }, [nodes, byKind]);

  // Live-motion treatments (S6.6) — honors prefers-reduced-motion (Flow L §8/§9/§11).
  const reducedMotion = usePrefersReducedMotion();
  const motion = useStatusMotion({ renders, connectionTotals, reducedMotion });

  const overallState: RollupState = parseRollupState(
    stream.lastFrame?.overall_state ?? snapshot?.overall_state ?? "unknown",
  );
  const asOf =
    stream.lastEventTime !== null
      ? formatAsOf(stream.lastEventTime)
      : snapshot
        ? formatAsOf(snapshot.generated_at)
        : "—";

  const announcement = useTransitionAnnouncer(nodes, renders);

  return (
    <div
      data-testid="system-status-panel"
      role="region"
      aria-label="System status"
      // ``system-status-scope`` applies the OQ-5 panel-scoped degraded amber
      // (globals.css) — classic amber inside this subtree, deep-orange left
      // untouched globally (the map needs it). Flow L §8; REQ-UI-046.
      className="system-status-scope flex h-full w-full flex-col bg-bg-base"
    >
      <StatusHeader streamState={stream.state} asOf={asOf} overall={overallState} />
      {stream.state === "reconnecting" && (
        <div
          role="status"
          data-testid="status-reconnect-banner"
          className="border-b border-warning/40 bg-warning/10 px-4 py-1.5 text-meta text-warning"
        >
          Live updates paused — reconnecting. Showing the last-known state as of {asOf}.
        </div>
      )}
      {/* ``closed`` is not a retry (REQ-STATUS-062): the browser stopped AND the
          hook's probe confirmed the cause was authentication (401/403 — an
          expired session or a withdrawn role). Saying "reconnecting" would
          promise a recovery that is not coming, so the stopped case gets its
          own banner and its own instruction. The instruction is only true
          because of the probe: a CLOSED EventSource on its own is also what an
          nginx 502 during a BFF restart looks like, and that case must never
          reach this banner (REQ-STATUS-062 amended 2026-10-19). */}
      {stream.state === "closed" && (
        <div
          role="alert"
          data-testid="status-stream-closed-banner"
          className="border-b border-destructive/50 bg-destructive/10 px-4 py-1.5 text-meta text-destructive"
        >
          Live updates stopped — the status stream was closed and is not retrying. Showing the
          last-known state as of {asOf}. Sign in again to resume.
        </div>
      )}
      {/* ``unavailable`` is the OTHER terminal state: the stream closed for a
          reason that was not authentication, the bounded reopen ladder ran to
          its 5-minute bound without a recovery, and the hook has stopped. The
          session is intact, so "sign in again" would be a false diagnosis on
          the surface that exists to diagnose; the honest instruction is a
          reload (which re-bootstraps and reopens). Same severity as ``closed``
          — the feed is equally dead — with its own text and testid so an
          operator, a screenshot, or an e2e spec can tell the two apart. */}
      {stream.state === "unavailable" && (
        <div
          role="alert"
          data-testid="status-stream-unavailable-banner"
          className="border-b border-destructive/50 bg-destructive/10 px-4 py-1.5 text-meta text-destructive"
        >
          Live updates stopped — the status stream has been unavailable for 5 minutes and is no
          longer retrying. Showing the last-known state as of {asOf}. Reload the page to try again.
        </div>
      )}

      <span className="sr-only" role="status" aria-live="polite" data-testid="status-announcer">
        {announcement}
      </span>

      {/* Topology-only, full width — the component/connection detail now renders
          in the shared Entity Details panel (ADR-024 §4/§5 amended 2026-07-24). */}
      <main className="min-h-0 min-w-0 flex-1 overflow-hidden">
        {query.isLoading ? (
          <LoadingState />
        ) : query.isError && snapshot === undefined ? (
          <ErrorState message={query.error.message} onRetry={() => void query.refetch()} />
        ) : (
          <div
            className={cn(
              "h-full w-full overflow-auto p-3 transition-opacity",
              // Dim whenever the feed is not delivering, not only while it is
              // retrying — a closed or unavailable stream is at least as stale
              // as a reconnecting one (REQ-STATUS-016).
              (stream.state === "reconnecting" ||
                stream.state === "closed" ||
                stream.state === "unavailable") &&
                "opacity-70",
            )}
          >
            <StatusDiagram
              nodes={nodes}
              edges={edges}
              renders={renders}
              selectedId={highlightedNodeId}
              selectedInterface={selectedInterface}
              onSelect={handleSelect}
              onBackgroundClick={clear}
              stacks={stacks}
              // The stack's own level decides the kind, so a peer icon can never
              // originate an interface selection carrying a peer's name — which
              // would resolve `interfaceComponentId(name)` to the bare name and
              // silently read an interface's samples for a peer.
              onSelectInterface={(parentId, name, level) =>
                inspectStatus(
                  level === "peer"
                    ? { kind: "status-peer", peerName: name, parentNodeId: parentId }
                    : { kind: "status-interface", interfaceName: name, parentNodeId: parentId },
                )
              }
              onOverflow={(parentId) => handleSelect(parentId)}
              transitioning={motion.transitioning}
              sustained={motion.sustained}
              bursts={motion.bursts}
              animate={motion.animate}
            />
          </div>
        )}
      </main>
    </div>
  );
}

/**
 * Announce a node's stoplight transition via ``aria-live`` (REQ-UI-046 / Flow L
 * §10). Returns the most recent transition text (e.g. "Message Bus degraded");
 * empty before the first change. Skips the initial render so a fresh paint
 * doesn't announce every node.
 */
function useTransitionAnnouncer(
  nodes: readonly TopologyNode[],
  renders: ReadonlyMap<string, NodeRender>,
): string {
  const prevRef = useRef<Map<string, RollupState> | null>(null);
  const [announcement, setAnnouncement] = useState("");

  useEffect(() => {
    const current = new Map<string, RollupState>();
    for (const node of nodes) {
      const render = renders.get(node.id);
      if (render) current.set(node.id, render.state);
    }
    const prev = prevRef.current;
    if (prev !== null) {
      const changes: string[] = [];
      for (const node of nodes) {
        const now = current.get(node.id);
        const before = prev.get(node.id);
        if (now !== undefined && before !== undefined && now !== before) {
          changes.push(`${node.name} ${rollupStateMeta(now).label.toLowerCase()}`);
        }
      }
      if (changes.length > 0) setAnnouncement(changes.join("; "));
    }
    prevRef.current = current;
  }, [nodes, renders]);

  return announcement;
}

/**
 * The panel header: title + overall pill + the Peer Manager affordance +
 * live/reconnecting indicator + "as of".
 *
 * The **Peer Manager** button lives here, not only on the egress nodes, because
 * a deployment that has configured no peer interfaces composes no peer-manager node
 * — and that is exactly the deployment where an operator needs to add one
 * (ADR-036 §D-5: the UI is the editing surface once a deployment is running).
 * It is offered to every authenticated role, because the surface itself is
 * all-roles-read (REQ-PEER-042); the *mutation* controls inside it are gated.
 */
function StatusHeader({
  streamState,
  asOf,
  overall,
}: {
  streamState: StatusStreamState;
  asOf: string;
  overall: RollupState;
}): React.ReactElement {
  // One row per state so the two terminal states cannot share a word: the
  // pill is the header's only client-observed signal, and REQ-STATUS-016/-049
  // forbid it reading as anything other than what the feed is doing.
  const PILL: Record<StatusStreamState, { dot: string; label: string }> = {
    connecting: { dot: "bg-decor-muted", label: "Connecting…" },
    live: { dot: "bg-positive", label: "UI Connected" },
    reconnecting: { dot: "bg-warning", label: "Reconnecting…" },
    closed: { dot: "bg-destructive", label: "Stream closed" },
    unavailable: { dot: "bg-destructive", label: "Stream unavailable" },
  };
  const { dot, label } = PILL[streamState];
  return (
    <header className="flex items-center gap-4 border-b border-border px-4 py-2">
      <h2 className="text-body font-medium text-text-primary">System</h2>
      <StatusPill state={overall} />
      {/* The header "Data egress…" button is REMOVED (bootstrap item 7). It existed
          because a deployment with zero configured peer interfaces composed no
          peer-manager node, leaving no route to create the first one — but the Peer Manager node
          is now STRUCTURAL and always composes (REQ-STATUS-041), so selecting it
          always reaches the "Peers…" affordance on its Selection Detail. That structural
          guarantee is what makes removing this safe; do not reintroduce the button
          without also reverting the node to config-variant. */}
      <span className="ml-auto flex items-center gap-2 text-meta text-text-secondary">
        <span className={cn("inline-block h-2 w-2 rounded-full", dot)} aria-hidden="true" />
        <span data-testid="status-stream-state">{label}</span>
        <span className="font-mono text-text-muted">as of {asOf}</span>
      </span>
    </header>
  );
}

// ---- Render-state stubs ------------------------------------------------------

function LoadingState(): React.ReactElement {
  return (
    <div
      data-testid="system-status-loading"
      role="status"
      aria-label="Loading system status"
      className="flex h-full w-full items-center justify-center bg-bg-base text-text-secondary"
    >
      <p className="text-body-sm">Loading system status…</p>
    </div>
  );
}

function ErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}): React.ReactElement {
  return (
    <div
      data-testid="system-status-error"
      role="alert"
      className="flex h-full w-full flex-col items-center justify-center gap-3 bg-bg-base"
    >
      <p className="text-body text-destructive">Could not load system status</p>
      <p className="px-6 text-center text-body-sm text-text-secondary">{message}</p>
      <button
        type="button"
        onClick={onRetry}
        className="rounded border border-border bg-bg-surface px-3 py-1 text-body-sm text-text-primary hover:bg-bg-elevated"
      >
        Retry
      </button>
    </div>
  );
}

function ForbiddenState(): React.ReactElement {
  return (
    <div
      data-testid="system-status-forbidden"
      role="alert"
      className="flex h-full w-full flex-col items-center justify-center gap-2 bg-bg-base text-text-secondary"
    >
      <p className="text-body text-text-primary">Access restricted</p>
      <p className="px-6 text-center text-body-sm text-text-muted">
        System status is available to every authenticated Meridian role. This session carries none —
        ask an administrator to assign one.
      </p>
    </div>
  );
}
